/**
 * Multi-select role picker used by the invite + edit user modals.
 *
 * The backend requires at least one role (CreateUserRequest @NotEmpty), so the
 * caller should block submit on an empty selection — this component just
 * toggles.
 */
import { ASSIGNABLE_ROLES, ROLE_META } from '../lib/roles';

export default function RoleSelector({ value = [], onChange, disabled }) {
  /**
   * Assignable roles, plus any the user already holds that are not.
   *
   * Without the second part a role that has been retired can never be taken
   * off someone: it stays checked in state but has no checkbox to untick. That
   * is exactly the role you need to remove during a migration.
   *
   * SUPER_ADMIN is excluded on purpose — the Team page preserves it separately
   * and it is not a dealership grant to give or take away here.
   */
  const rendered = [
    ...ASSIGNABLE_ROLES,
    ...value.filter((r) => !ASSIGNABLE_ROLES.includes(r) && r !== 'SUPER_ADMIN'),
  ];

  const toggle = (role) => {
    if (disabled) return;
    onChange(value.includes(role) ? value.filter(r => r !== role) : [...value, role]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rendered.map((role) => {
        const meta = ROLE_META[role] || { label: role, desc: '' };
        const checked = value.includes(role);
        const retiring = !ASSIGNABLE_ROLES.includes(role);
        return (
          <label
            key={role}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '8px 10px',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-sm)',
              background: checked ? 'var(--accent-glow)' : 'var(--bg-glass)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(role)}
              style={{ marginTop: 2 }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                {meta.label}
                {/* Say why it is here. A role that cannot be granted but is still
                    shown looks like a mistake unless it explains itself. */}
                {retiring && (
                  <span className="pill warning" style={{ marginLeft: 8, fontSize: 10 }}>
                    being retired
                  </span>
                )}
              </span>
              <span className="mono text-xs text-muted">
                {retiring ? 'Held today — untick to remove. Cannot be granted again.' : meta.desc}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
